import React, { FormEvent } from 'react'
import { graphql } from 'react-apollo'
import MediaQuery from 'react-responsive'
import { withRouter } from 'react-router-dom'

import { Bodytext, Button, TextareaInput } from '@acorns/web-components'
import { withAnalyticsClient, withEnv } from '@acorns/web-utils'
import { withFormik } from 'formik'
import { isMobile } from 'is-mobile'
import { filter, pathOr } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'
import styled from 'styled-components'

import SelectInput from 'components/Form/SelectInput'
import FullscreenTakeover from 'components/FullscreenTakeover'
import useWindowDimensions from 'hooks/useWindowDimensions'
import {
  currentSubscriptionType,
  requestCloseAccountCancelledFields,
} from 'pages/settings/my-subscription/segment'
import { withEdgeCaseProductInfo } from 'pages/settings/my-subscription/tier-subscription-center/utils'
import themeValues, { style } from 'theme'
import { PageAnalytics } from 'utils/analytics'
import { shuffle } from 'utils/array/shuffle'
import { HELP_TEXT_CLOSE_ACCOUNT_DETERRENTS } from 'utils/testing/testIDs'
import withAnalyticsIdentity from 'utils/with-analytics-identity'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import { StyledCard } from '../components'
import {
  FormValues,
  handleSubmit,
  mapPropsToValues,
  validationSchema,
} from './form'
import GrowArticle, { getZestyData } from './grow-articles/growArticles'
import helpText from './helpText'
import { closeAccountQuery } from './queries'

interface IReason {
  label: string
  value: string
}

type IProps = {
  accountNumberLastFour: string
  balance: number
  subscriptionType: string
  data?: any
  exitWebViewCancel: () => void
  fundingSourceName: string
  handleChange: (event: FormEvent<HTMLTextAreaElement>) => void
  handleLogout: () => void
  handleReasonChange: (value: string) => void
  handleSubmit: () => void
  handleCancelSelect: () => void
  isValid: boolean
  isWebView: boolean
  loadingAllAccounts: boolean
  reasons: IReason[]
  shuffledReasons: IReason[]
  requiresSupportToClose: boolean
  showModal: boolean
  values: FormValues
}

const Section = styled.div`
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 44px;
`

const TopSection = styled.div`
  padding-left: 50px;
  padding-right: 50px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-bottom: 1px solid ${style((theme) => theme.colors.ivory)};
    padding: 20px;
  }
`
const BottomSection = styled.div`
  padding-left: 50px;
  padding-right: 50px;
  background-color: ${style((theme) => theme.colors.white)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 20px;
    width: 100%;
  }
`
const Card = styled(StyledCard)<{ innerHeight?: number }>`
  text-align: left;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 0px;
    &&& {
      height: ${(props) => `calc(${props.innerHeight}px - 78px)`};
      justify-content: flex-start;
      overflow: scroll;
    }
  }
`
const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  text-align: left;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 0px;
    &&& {
      height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
      justify-content: flex-start;
      overflow: scroll;
      border-radius: 0;
    }
  }
`

const Text = styled(Bodytext)`
  padding-left: 20px;
  padding-right: 20px;
  text-align: center;
  margin-bottom: 30px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 0px;
    margin-bottom: 0;
  }
`

const Bold = styled.span`
  font-weight: 600;
`

const HelpText = styled.div`
  background-color: ${style((theme) => theme.colors.ivory)};
  border-radius: ${style((theme) => theme.styles.borderRadius)};
  color: ${style((theme) => theme.colors.slate)};
  font-size: 14px;
  line-height: 19px;
  margin-bottom: 32px;
  padding: 13px 16px;
`

const Actions = styled.div`
  background-color: ${style((theme) => theme.colors.white)};
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    display: flex;
    justify-content: flex-end;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: auto;
    padding: 20px 20px 75px 20px;
    width: 100%;
  }
`

const CloseButton = styled(Button)`
  &&& {
    min-width: 150px;
  }

  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
  }
`

interface IMessageProps {
  show: boolean
}

const MessageContainer = styled<IMessageProps, any>('div')`
  min-height: 42px;
  opacity: 0;
  transition: opacity 150ms;

  ${(props: any) =>
    props.show &&
    `
    opacity: 1;
  `};
`

const CloseReasonDropdown = styled(SelectInput)`
  max-height: 45vh;
`
const StyledTextareaInput = styled(TextareaInput)`
  &:first-child {
    &&& {
      font-weight: 600;
    }
  }
`
export const Message = ({
  balance,
  fundingSourceName,
  accountNumberLastFour,
}) => (
  <MessageContainer show={fundingSourceName}>
    <Text style={{ minHeight: '42px' }}>
      Your account balance of <Bold>{balance}</Bold> will be transferred to your{' '}
      <Bold>
        {fundingSourceName} ({accountNumberLastFour})
      </Bold>
      . Account balance may change due to market fluctuations.
    </Text>
  </MessageContainer>
)

export const enhance = compose<IProps, {}>(
  withEnv,
  withAnalyticsClient,
  withRouter,
  withEdgeCaseProductInfo,
  withProps(({ userAccountState }) => ({
    isMobileDevice: isMobile(),
    isWebView: window.location.pathname.startsWith('/wv/'),
    hasLaterAccount: userAccountState?.laterAccount?.id !== undefined,
    hasSpendAccount: userAccountState?.spendAccount?.active,
    hasLaterBalance: userAccountState?.laterAccount?.balance > 0,
  })),
  graphql(closeAccountQuery, {
    props: ({ data }) => ({
      data,
      reasons: pathOr([], ['allCloseAccountReasons'], data).map(
        ({ key, label }) => ({
          label,
          value: key,
        }),
      ),
    }),
  }),
  withAnalyticsIdentity,
  withProps<any, any>((props) => {
    const isWebView = window.location.pathname.startsWith('/wv/')
    const shuffledReasons = filter(
      (reason: IReason) => reason.value !== 'other',
    )(shuffle(props.reasons))
    shuffledReasons.push({
      label: 'Other',
      value: 'other',
    })
    const subscriptionType = currentSubscriptionType(props)
    const backOverride = props.isWebView
      ? '/wv/my-subscription'
      : '/settings/my-subscription'

    return {
      isWebView,
      shuffledReasons,
      subscriptionType,
      backOverride,
    }
  }),
  withState('showGrowCard', 'setShowGrowCard', false),
  withState('currentArticle', 'setCurrentArticle', null),
  withState('articleLoading', 'setArticleLoading', false),
  withFormik({
    mapPropsToValues,
    validationSchema,
    handleSubmit,
  }),
  withHandlers({
    exitWebViewSuccess: () => () =>
      window.location.assign('acorns://event?flow_complete=true&error=false'),
  }),
  withHandlers({
    handleReasonChange:
      ({ setFieldValue }) =>
      (field, value) => {
        setFieldValue(field, value)
      },
    handleLogout:
      ({ exitWebViewSuccess, history, isWebView }) =>
      () => {
        if (isWebView) {
          exitWebViewSuccess()
        } else {
          history.push('/logout')
        }
      },
    handleCancelSelect:
      ({ history, isWebView, analytics, values }) =>
      () => {
        analytics.track(
          'Button Tapped',
          requestCloseAccountCancelledFields(values),
        )
        history.push(
          isWebView ? '/wv/cancel-subscription' : '/cancel-subscription',
        )
      },
    handleShowArticle:
      ({
        setArticleLoading,
        setShowGrowCard,
        setCurrentArticle,
        isWebView,
        history,
        env,
      }) =>
      (id) => {
        // pop up modal for web view article & route to grow article for regular web
        if (isWebView) {
          setArticleLoading(true)
          getZestyData(id, env)
            .then((article) => {
              setCurrentArticle(article)
            })
            .then(() => {
              setShowGrowCard(true)
              setArticleLoading(false)
            })
        } else {
          history.push(`/grow/${id}`, { referrer: location.pathname })
        }
      },
    handleCloseGrowArticle:
      ({ setCurrentArticle, setShowGrowCard }) =>
      () => {
        setCurrentArticle(null)
        setShowGrowCard(false)
      },
  }),
)

export const Page: React.FC<IProps> = (props) => {
  const CardComponent = props.isWebView ? WVCard : Card
  const { height } = useWindowDimensions()
  return (
    <>
      {props.isWebView && (
        <>
          {!props.data.loading && <PageAnalytics />}
          <FullscreenTakeover innerHeight={height}>
            <CloseAccountWrapper
              isWebView={props.isWebView}
              action={{
                onClick: props.showGrowCard
                  ? props.handleCloseGrowArticle
                  : props.handleCancelSelect,
                shouldShowCloseIcon: false,
              }}
              link={{
                onClick: !props.showGrowCard ? props.handleCancelSelect : null,
                text: !props.showGrowCard ? 'Cancel' : null,
              }}
              actionStripTitle={'Reason for canceling'}
              showCancellable={true}
              handleCancel={props.handleCancelSelect}
            >
              {props.showGrowCard && GrowArticle(props)}
              {!props.showGrowCard && (
                <CardComponent isWebView={props.isWebView} innerHeight={height}>
                  <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
                    <TopSection>
                      <Text>
                        So we can make Acorns better, can you tell us why you
                        want to cancel your subscription?
                      </Text>
                    </TopSection>
                  </MediaQuery>
                  <MediaQuery minWidth={themeValues.breakpoints.sm}>
                    <Section>
                      So we can make Acorns better, can you tell us why you want
                      to cancel your subscription?
                    </Section>
                  </MediaQuery>

                  <BottomSection>
                    <CloseReasonDropdown
                      testId="reason"
                      label="Reason for canceling subscription"
                      name="reason"
                      onChange={props.handleReasonChange}
                      options={props.shuffledReasons}
                      placeholder={'Select Reason'}
                      value={props.values.reason}
                      disabled={props.loadingAllAccounts}
                      useNative={false}
                      hideOverlay={true}
                    />
                    {helpText[props.values.reason] && (
                      <HelpText
                        data-testid={HELP_TEXT_CLOSE_ACCOUNT_DETERRENTS}
                      >
                        {helpText[props.values.reason](
                          props.isWebView,
                          props.history,
                          props.handleShowArticle,
                        )}
                      </HelpText>
                    )}

                    <StyledTextareaInput
                      id="comments"
                      label="Tell us more"
                      name="comments"
                      onChange={props.handleChange}
                      placeholder="Leave a comment"
                      value={props.values.comments}
                    />
                  </BottomSection>
                  <Actions>
                    <CloseButton
                      disabled={
                        !props.isValid ||
                        props.loadingAllAccounts ||
                        (props.values.reason === 'other' &&
                          !props.values.comments)
                      }
                      onPress={props.handleSubmit}
                    >
                      Next
                    </CloseButton>
                  </Actions>
                </CardComponent>
              )}
            </CloseAccountWrapper>
          </FullscreenTakeover>
        </>
      )}

      {!props.isWebView && (
        <CloseAccountWrapper
          isWebView={props.isWebView}
          actionStripTitle="Reason for canceling"
          actionText="Go back"
          backOverride={props.backOverride}
          showCancellable={true}
          handleCancel={props.handleCancelSelect}
        >
          {props.showGrowCard && GrowArticle(props)}
          {!props.showGrowCard && (
            <Card>
              <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
                <TopSection>
                  <Bodytext>
                    So we can make Acorns better, can you tell us why you want
                    to cancel your subscription?
                  </Bodytext>
                </TopSection>
              </MediaQuery>
              <MediaQuery minWidth={themeValues.breakpoints.sm}>
                <Section>
                  So we can make Acorns better, can you tell us why you want to
                  cancel your subscription?
                </Section>
              </MediaQuery>

              <BottomSection>
                <CloseReasonDropdown
                  testId="reason"
                  label="Reason for canceling subscription"
                  name="reason"
                  onChange={props.handleReasonChange}
                  options={props.shuffledReasons}
                  placeholder={'Select Reason'}
                  value={props.values.reason}
                  disabled={props.loadingAllAccounts}
                  useNative={false}
                />
                {helpText[props.values.reason] && (
                  <HelpText data-testid={HELP_TEXT_CLOSE_ACCOUNT_DETERRENTS}>
                    {helpText[props.values.reason](
                      props.isWebView,
                      props.history,
                      props.handleShowArticle,
                    )}
                  </HelpText>
                )}

                <StyledTextareaInput
                  id="comments"
                  label="Tell us more"
                  name="comments"
                  onChange={props.handleChange}
                  placeholder="Leave a comment"
                  value={props.values.comments}
                />

                <Actions>
                  <CloseButton
                    disabled={
                      !props.isValid ||
                      props.loadingAllAccounts ||
                      (props.values.reason === 'other' &&
                        !props.values.comments)
                    }
                    onPress={props.handleSubmit}
                  >
                    Next
                  </CloseButton>
                </Actions>
              </BottomSection>
            </Card>
          )}
        </CloseAccountWrapper>
      )}
    </>
  )
}

export default enhance(Page)
